'use client';

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { useComments } from '../../hooks/use-comments';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { VerificationBadge } from '../../components/feedback/verification-badge';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../hooks/use-auth';
import { CommentItem } from '../../components/blog/comment-item';
import { useGetArticleById } from '../../hooks/use-articles';
import { Separator } from '../../components/ui/separator';
import { CommentMintingLoader } from '../../components/modals/comment-minting-loader';
import { useAuthStore } from '../../stores';
import { useGetUserById, useCheckUserLogin } from '../../hooks/use-user';
import { useLikeArticle, useDislikeArticle } from '../../hooks/use-articles';
import { extractOidFromSlug, getArticlePath } from '../../utils/slug';
import { ShareButton } from '../../components/feedback/share-button';

export default function BlogPostPage() {
  const { postId: postIdParam } = useParams();
  // Extract OID from SEO-friendly slug URL (e.g., "my-article-title-abc123" -> "abc123")
  const postId = extractOidFromSlug(postIdParam);
  const [showComments, setShowComments] = useState(true);
  const [commentContent, setCommentContent] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [transactionHash, setTransactionHash] = useState('');
  const [mintingError, setMintingError] = useState(null);
  const [mintingStatus, setMintingStatus] = useState('idle'); // idle, minting, success, error

  // Get wallet address from Passport SSO auth store (replaces useActiveAccount)
  const { walletAddress } = useAuthStore();
  const {
    data: currentUser,
    isLoading: isLoadingCurrentUser,
    error: currentUserError,
  } = useGetUserById(walletAddress);

  // Use TanStack Query hook for article by oid
  const {
    data: article,
    isLoading,
    error,
    likeArticle,
  } = useGetArticleById(postId);

  const {
    data: author,
    isLoading: isLoadingAuthor,
    error: userError,
  } = useGetUserById(article?.creator);

  // Get comments
  const { comments, isCommentsLoading, addComment } = useComments(postId);

  const likeArticleMutation = useLikeArticle();
  const dislikeArticleMutation = useDislikeArticle();

  // Handle like article
  const handleLike = () => {
    if (!displayArticle?.oid || !walletAddress) return;

    likeArticleMutation.mutate({
      articleId: displayArticle.oid,
      uOID: currentUser.oid,
      creator: walletAddress,
    });
  };

  // Handle dislike article
  const handleDislike = () => {
    if (!displayArticle?.oid || !walletAddress) return;
    dislikeArticleMutation.mutate({
      articleId: displayArticle.oid,
      uOID: currentUser.oid,
      creator: walletAddress,
    });
  };

  // Handle add comment
  const handleCommentSubmit = () => {
    if (!commentContent.trim()) return;

    setIsSubmittingComment(true);
    setMintingStatus('minting');
    setMintingError(null);
    setTransactionHash('');

    // Simulate getting a transaction hash after a short delay
    setTimeout(() => {
      setTransactionHash(
        '0x' +
          Math.random().toString(16).substring(2, 14) +
          Math.random().toString(16).substring(2, 14)
      );
    }, 2000);

    addComment.mutate(
      {
        content: commentContent,
        commentType: 'particleComment',
        creator: walletAddress || null,
      },
      {
        onSuccess: (data) => {
          setCommentContent('');
          setIsSubmittingComment(false);
          setMintingStatus('success');
          setMintingError(null);
        },
        onError: (error) => {
          console.error('Failed to post comment:', error);
          setMintingError(
            error.message || 'Failed to mint comment NFT. Please try again.'
          );
          setIsSubmittingComment(false);
          setMintingStatus('error');
        },
      }
    );
  };

  // Handle retry
  const handleRetry = () => {
    setMintingError(null);
    handleCommentSubmit();
  };

  const displayArticle = article;

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-black">
        <Navbar />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-zinc-800 rounded w-3/4 mb-4"></div>
            <div className="flex items-center gap-2 mb-6">
              <div className="h-10 w-10 bg-zinc-800 rounded-full"></div>
              <div className="h-4 bg-zinc-800 rounded w-32"></div>
            </div>
            <div className="h-64 bg-zinc-800 rounded mb-6"></div>
            <div className="space-y-3">
              <div className="h-4 bg-zinc-800 rounded"></div>
              <div className="h-4 bg-zinc-800 rounded"></div>
              <div className="h-4 bg-zinc-800 rounded"></div>
              <div className="h-4 bg-zinc-800 rounded w-5/6"></div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !displayArticle) {
    return (
      <div className="flex min-h-screen flex-col bg-black">
        <Navbar />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="text-center py-12 bg-zinc-900 rounded-lg border border-white/10">
            <h1 className="text-2xl font-bold mb-4 text-white">
              Article not found
            </h1>
            <p className="text-zinc-400 mb-6">
              The article you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/blog">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                Back to Blog
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formattedDate = displayArticle.createdAt
    ? formatDistanceToNow(new Date(displayArticle.createdAt), {
        addSuffix: true,
      })
    : 'recently';

  return (
    <div className="flex min-h-screen flex-col bg-black text-white font-sans selection:bg-violet-500/30 relative">
      <Navbar />

      {/* Gradient Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      {/* Article cover image */}
      {displayArticle.banner && (
        <div className="container mx-auto px-4 pb-6 pt-24 relative z-10">
          <div className="aspect-video w-full max-h-[450px] overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-violet-500/10">
            <img
              src={displayArticle.coverImage || '/placeholder.svg'}
              alt={'Article Cover Image'}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* NFT Minting Loader */}
      <CommentMintingLoader
        user={currentUser}
        isOpen={
          mintingStatus === 'minting' ||
          mintingStatus === 'success' ||
          mintingStatus === 'error'
        }
        onClose={() => setMintingStatus('idle')}
        commentContent={commentContent}
        transactionHash={transactionHash}
        error={mintingError}
        onRetry={handleRetry}
        status={mintingStatus}
      />

      <main className="flex-1 relative z-10">
        {/* Article header */}
        <div className="bg-black/40 backdrop-blur-md border-b border-white/5 py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-black mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-zinc-400 leading-tight">
                {displayArticle.title || 'Untitled'}
              </h1>

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                <div className="flex items-center gap-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Avatar className="h-12 w-12 cursor-pointer border border-white/10 ring-2 ring-transparent hover:ring-violet-500/30 transition-all">
                        <AvatarImage
                          src={'/placeholder.svg'}
                          alt={displayArticle.creator}
                        />
                        <AvatarFallback className="bg-zinc-800 text-zinc-400">
                          {displayArticle.creator?.toString().charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 bg-zinc-900/95 backdrop-blur-xl border-white/10 text-white p-0 overflow-hidden">
                      <div className="relative h-20 bg-gradient-to-r from-violet-600 to-cyan-600 opacity-20"></div>
                      <div className="flex flex-col items-center text-center p-6 -mt-10">
                        <Avatar className="h-20 w-20 mb-4 border-4 border-zinc-900">
                          <AvatarImage
                            src={'/placeholder.svg'}
                            alt={displayArticle.creator}
                          />
                          <AvatarFallback className="bg-zinc-800 text-zinc-400">
                            {displayArticle.creator?.toString().charAt(0) ||
                              'U'}
                          </AvatarFallback>
                        </Avatar>
                        <h3 className="font-bold text-lg text-white">
                          {displayArticle.creator}
                        </h3>
                        <p className="text-sm text-zinc-400 mb-4">
                          {displayArticle.visibility ||
                            'Writer and contributor'}
                        </p>
                        <div className="flex items-center gap-2 mb-6 text-zinc-500 bg-white/5 px-3 py-1.5 rounded-full border border-white/5">
                          <span className="text-xs font-medium text-violet-400">
                            {displayArticle.chainName}
                          </span>
                          <span className="text-xs">•</span>
                          <span className="text-xs font-medium text-cyan-400">
                            {displayArticle.chainType}
                          </span>
                        </div>
                        <Button className="w-full bg-white text-black hover:bg-zinc-200 font-bold rounded-xl">
                          Follow
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <div>
                    <h2 className="font-bold text-white text-lg">
                      {author.username}
                    </h2>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-zinc-400">
                        Published {formattedDate}
                      </p>
                      <span className="text-zinc-600 hidden sm:inline">•</span>
                      <VerificationBadge
                        compact={true}
                        creator={displayArticle.creator}
                        mintInfo={displayArticle.mintInformation}
                        oid={displayArticle.oid}
                        ual={displayArticle.ual}
                        showText={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Article actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLike}
                    disabled={likeArticleMutation.isLoading}
                    className="border-white/10 bg-white/5 text-zinc-300 hover:text-white hover:bg-white/10 hover:border-violet-500/30 transition-all rounded-xl"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill={displayArticle.likes > 0 ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={`mr-2 ${
                        displayArticle.likes > 0 ? 'text-red-500' : ''
                      }`}
                    >
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                    </svg>
                    {displayArticle.likes || 0}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDislike}
                    disabled={dislikeArticleMutation.isLoading}
                    className="border-white/10 bg-white/5 text-zinc-300 hover:text-white hover:bg-white/10 hover:border-violet-500/30 transition-all rounded-xl"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <path d="M10 14v7a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-7" />
                      <path d="M19 10V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v5" />
                    </svg>
                    {displayArticle.dislikes || 0}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowComments(!showComments)}
                    className="border-white/10 bg-white/5 text-zinc-300 hover:text-white hover:bg-white/10 hover:border-violet-500/30 transition-all rounded-xl"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                    </svg>
                    {comments?.length || 0}
                  </Button>

                  {/* Share Button */}
                  <ShareButton
                    title={displayArticle.title}
                    description={
                      displayArticle.description ||
                      displayArticle.text
                        ?.replace(/<[^>]+>/g, '')
                        .substring(0, 150)
                    }
                    url={`${window.location.origin}${getArticlePath(
                      displayArticle
                    )}`}
                    showText={false}
                  />
                </div>
              </div>

              <div className="flex gap-2 flex-wrap">
                {Array.isArray(article.topics) &&
                  article.topics.map((topic, idx) => {
                    let topicNames = [];
                    if (typeof topic === 'string') {
                      topicNames = topic.split(',').map((t) => t.trim());
                    } else if (topic?.slug || topic?.name) {
                      topicNames = [(topic.slug || topic.name).trim()];
                    }
                    return topicNames.map(
                      (name) =>
                        name && (
                          <Badge
                            key={name + idx}
                            variant="secondary"
                            className="bg-white/5 text-zinc-300 hover:bg-white/10 hover:text-white border border-white/5 px-3 py-1 rounded-lg transition-colors"
                          >
                            {name}
                          </Badge>
                        )
                    );
                  })}
              </div>
            </div>
          </div>
        </div>

        {/* Article content */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-lg prose-invert max-w-none text-zinc-300 prose-headings:text-white prose-a:text-violet-400 prose-a:no-underline hover:prose-a:underline prose-strong:text-white prose-code:text-violet-300 prose-code:bg-violet-900/20 prose-code:px-1 prose-code:rounded prose-pre:bg-zinc-900/50 prose-pre:border prose-pre:border-white/10">
              {/* Use dangerouslySetInnerHTML with DOMPurify for XSS protection */}
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    displayArticle.text || displayArticle.htmlContent || ''
                  ),
                }}
              />
            </div>

            {/* Comments Section */}
            {showComments && (
              <div className="mt-16 pt-10 border-t border-white/5">
                <h2 className="text-2xl font-bold mb-8 text-white flex items-center gap-3">
                  <div className="p-2 bg-violet-500/10 rounded-lg">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-violet-400"
                    >
                      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                    </svg>
                  </div>
                  Discussion
                </h2>

                {/* Comment Form */}
                <div className="bg-zinc-900/30 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-10 shadow-xl">
                  <h3 className="text-lg font-medium mb-4 text-white">
                    Leave a comment
                  </h3>
                  <div className="flex gap-4">
                    <Avatar className="h-10 w-10 border border-white/10">
                      <AvatarImage
                        src={currentUser?.avatar || '/placeholder.svg'}
                        alt={currentUser?.username || 'User'}
                      />
                      <AvatarFallback className="bg-zinc-800 text-zinc-400">
                        {currentUser?.username?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <Textarea
                        placeholder="Share your thoughts..."
                        value={commentContent}
                        onChange={(e) => setCommentContent(e.target.value)}
                        className="min-h-[100px] bg-black/50 border-white/10 text-white placeholder:text-zinc-500 focus:border-violet-500 focus:ring-violet-500/20 rounded-xl resize-none"
                      />
                      <div className="mt-4 flex justify-end">
                        <Button
                          onClick={handleCommentSubmit}
                          disabled={
                            !commentContent.trim() || isSubmittingComment
                          }
                          className="bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-500 hover:to-cyan-500 text-white font-semibold shadow-lg shadow-violet-500/20 rounded-xl px-6"
                        >
                          {isSubmittingComment
                            ? 'Preparing...'
                            : 'Post Comment'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comments List */}
                <div>
                  {isCommentsLoading ? (
                    <div className="text-center py-12">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500"></div>
                      <p className="mt-4 text-zinc-400">Loading comments...</p>
                    </div>
                  ) : comments && comments.length > 0 ? (
                    <div className="space-y-6">
                      {comments
                        .filter((comment) => !comment.parentId)
                        .map((comment) => (
                          <CommentItem
                            key={comment.oid}
                            comment={comment}
                            postId={postId}
                            currentUser={currentUser}
                          />
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white/5 rounded-2xl border border-white/5 border-dashed">
                      <p className="text-zinc-400">
                        No comments yet. Be the first to start the discussion!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
